import React from 'react';
import axios from '../../../axios-config.js';
import Draggable from 'react-draggable';
import {connect} from 'react-redux';
import {closeModal} from '../../../Redux/actions/ui.js';
import {loadGeojson, clearGeojson, toggleMarkers, showMarkers, toggleTenementsLayer, hideTenementsLayer, setNextBounds} from '../../../Redux/actions/mapData.js';
import { createTenementHolderCsvUrl, createTenementHolderFeatureUrl } from '../../Map/utils/tenementsGeoServer.js';

import DatePicker from 'react-datepicker';

const geoServerRequestOptions = {
  transformRequest: [(data, headers = {}) => {
    delete headers.Authorization;
    return data;
  }]
};

//https://stackoverflow.com/questions/23593052/format-javascript-date-as-yyyy-mm-dd
function formatDate(date) {
    var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

    if (month.length < 2)
        month = '0' + month;
    if (day.length < 2)
        day = '0' + day;

    return [year, month, day].join('-');
}

function alphaSort(a, b) {
  return String(a).localeCompare(String(b));
}

function isFiniteCoordinate(value) {
  return Number.isFinite(Number(value));
}

function collectCoordinatePairs(coordinates, pairs = []) {
  if (!Array.isArray(coordinates)) {
    return pairs;
  }

  if (coordinates.length >= 2 && isFiniteCoordinate(coordinates[0]) && isFiniteCoordinate(coordinates[1])) {
    pairs.push([Number(coordinates[0]), Number(coordinates[1])]);
    return pairs;
  }

  coordinates.forEach((child) => collectCoordinatePairs(child, pairs));
  return pairs;
}

function calculateBoundsFromFeatures(features = []) {
  const pairs = [];

  features.forEach((feature) => {
    if (feature && feature.geometry && feature.geometry.coordinates) {
      collectCoordinatePairs(feature.geometry.coordinates, pairs);
    }
  });

  const validPairs = pairs.filter(([lon, lat]) => lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90);

  if (validPairs.length === 0) {
    return null;
  }

  const bounds = validPairs.reduce((acc, [lon, lat]) => ({
    minLat: Math.min(acc.minLat, lat),
    minLon: Math.min(acc.minLon, lon),
    maxLat: Math.max(acc.maxLat, lat),
    maxLon: Math.max(acc.maxLon, lon),
  }), {
    minLat: Infinity,
    minLon: Infinity,
    maxLat: -Infinity,
    maxLon: -Infinity,
  });

  return [[bounds.minLat, bounds.minLon], [bounds.maxLat, bounds.maxLon]];
}

export function calculateFeatureBounds(feature) {
  return calculateBoundsFromFeatures(feature ? [feature] : []);
}

export function calculateFeatureCollectionBounds(featureCollection) {
  const features = featureCollection && Array.isArray(featureCollection.features) ? featureCollection.features : [];
  return calculateBoundsFromFeatures(features);
}

function parseCsvLine(line) {
  const values = [];
  let current = '';
  let quoted = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && quoted && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === ',' && !quoted) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

export function parseHolderCsv(data) {
  if (typeof data !== 'string') {
    return [];
  }

  const holders = {};

  data.split(/\r?\n/).forEach((line) => {
    if (!line.trim()) {
      return;
    }

    const values = parseCsvLine(line);
    const holder = String(values[values.length - 1] || '').replace(/"/g, '').trim();

    if (holder && holder.toLowerCase() !== 'holder1' && !holders[holder]) {
      holders[holder] = true;
    }
  });

  return Object.keys(holders).sort(alphaSort);
}

export function normaliseTenementsList(data) {
  const list = Array.isArray(data)
    ? data
    : data && Array.isArray(data.tenements)
      ? data.tenements
      : data && Array.isArray(data.data)
        ? data.data
        : [];

  return list.map((tenement) => {
    if (typeof tenement === 'string' || typeof tenement === 'number') {
      return { id: String(tenement), name: String(tenement) };
    }

    if (!tenement || typeof tenement !== 'object') {
      return null;
    }

    const id = tenement.id || tenement.tenement_id || tenement.tenementId || tenement.value || tenement.name;
    const name = tenement.name || tenement.title || tenement.label || tenement.tenement_name || tenement.tenementName || id;

    return id ? { id: String(id), name: String(name) } : null;
  }).filter(Boolean).sort((a, b) => alphaSort(a.name, b.name));
}

function normaliseDateList(data) {
  const list = data && Array.isArray(data.dataAvailable)
    ? data.dataAvailable
    : data && Array.isArray(data.dates)
      ? data.dates
      : Array.isArray(data)
        ? data
        : [];

  return list.map((value) => new Date(value)).filter((date) => !Number.isNaN(date.getTime()));
}

function getFeatureCollection(data, name = 'Tenements') {
  const features = data && data.type === 'FeatureCollection' && Array.isArray(data.features)
    ? data.features
    : data && Array.isArray(data.features)
      ? data.features
      : data && data.type === 'Feature'
        ? [data]
        : [];

  const validFeatures = features.filter((feature) => feature && feature.type === 'Feature' && feature.geometry);

  if (validFeatures.length === 0) {
    return null;
  }

  return {
    type: 'FeatureCollection',
    name: data && data.name ? data.name : name,
    crs: data && data.crs,
    features: validFeatures,
  };
}

function statusText(error, fallback) {
  if (error && error.response && error.response.status) {
    return `${fallback} (${error.response.status})`;
  }

  return fallback;
}

let CloseButton = (props) => {
  return (
    <div onClick={props.handleClick} className="titlebarCloseButton"><i className="material-icons notranslate">close</i></div>
  )
}

let MinimizeButton = (props) => {
  return (
    <div className="titlebarMinMaxButton" onClick={props.toggleMinimize}>
      <i className="material-icons notranslate">{props.minimized ? "crop_square" : "remove"}</i>
    </div>
  )
}

function WindowControls(props) {
  return (
    <div className="windowControlsContainer">
      <MinimizeButton minimized={props.minimized} toggleMinimize={props.toggleMinimize} />
      <CloseButton handleClick={props.handleClose} />
    </div>
  )
}

function MapControls(props) {
  return (
    <div className="TenementMapControls">
      <button type="button" className="MapControlsButton" onClick={props.toggleMarkers}>{props.markersVisible ? "Hide" : "Show"} project markers</button>
      <button type="button" className="MapControlsButton" onClick={props.toggleTenementsLayer}>{props.tenementsLayerVisible ? "Hide" : "Show"} today's tenements</button>
    </div>
  )
}

function StatusMessage({ type = 'info', children }) {
  if (!children) {
    return null;
  }

  return <div className={`TenementDirectoryStatus ${type}`}>{children}</div>;
}

class TenementDirectory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      title:"Tenements Directory",
      minimized:false
    }
    this.setTitle = this.setTitle.bind(this);
    this.toggleMinimize = this.toggleMinimize.bind(this);
  }

  toggleMinimize(force) {
    if (typeof(force) === "boolean") {
      this.setState({minimized:force});
    } else {
      this.setState({minimized:!this.state.minimized});
    }
  }

  setTitle(newTitle) {
    this.setState({title:newTitle})
  }

  componentWillUnmount() {
    this.props.showMarkers();
    this.props.hideTenementsLayer();
  }

  componentDidMount() {
    if (!this.props.tenementsLayerVisible) {
      this.props.toggleTenementsLayer();
    }
  }

  render () {
    return (
      <Draggable handle=".popupDragHandle">
        <div className={"WorldEditorContainer TenementDirectoryWindow" + (this.state.minimized ? " minimized" : "")}>
          <div className="popupDragHandle"><div className="TenementDirectoryTitle">{this.state.title}</div><WindowControls minimized={this.state.minimized} toggleMinimize={this.toggleMinimize} handleClose={this.props.closeModal} /></div>
          <TenementsWizard setNextBounds={this.props.setNextBounds} loadGeojson={this.props.loadGeojson} clearGeojson={this.props.clearGeojson} />
          <MapControls markersVisible={this.props.markersVisible} tenementsLayerVisible={this.props.tenementsLayerVisible} toggleMarkers={this.props.toggleMarkers} toggleTenementsLayer={this.props.toggleTenementsLayer} />
        </div>
      </Draggable>
    )
  }
}

class TenementSearch extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      holders:[],
      searchResults:[],
      selectedHolder:"",
      loading:true,
      holderLoading:false,
      error:null,
      message:null
    };
    this.searchHolders = this.searchHolders.bind(this);
    this.getHolderFeature = this.getHolderFeature.bind(this);
  }

  componentDidMount() {
    axios.get(createTenementHolderCsvUrl(), geoServerRequestOptions)
    .then(response => {
      const sortedHolders = parseHolderCsv(response.data);
      this.setState({
        searchResults: sortedHolders,
        holders: sortedHolders,
        loading: false,
        error: sortedHolders.length === 0 ? 'No tenement holders were returned.' : null
      });
    })
    .catch(error => {
      console.error('Error loading tenement holders:', error);
      this.setState({loading:false, error: statusText(error, 'Unable to load tenement holders.')});
    })
  }

  searchHolders(value) {
    const query = String(value || '').trim().toLowerCase();
    const searchResults = query.length === 0
      ? this.state.holders
      : this.state.holders.filter(holder => holder.toLowerCase().includes(query));

    this.setState({searchResults});
  }

  getHolderFeature(value) {
    if (!value || this.state.holderLoading) {
      return;
    }

    this.setState({holderLoading:true, error:null, message:null});

    axios.get(createTenementHolderFeatureUrl(value), geoServerRequestOptions)
    .then(response => {
      const featureCollection = getFeatureCollection(response.data, value);

      if (!featureCollection) {
        this.setState({
          selectedHolder:value,
          error:'No map features were found for that holder.',
          holderLoading:false
        });
        return;
      }

      this.props.clearGeojson();
      this.props.loadGeojson(featureCollection);

      const bounds = calculateFeatureCollectionBounds(featureCollection);
      if (bounds) {
        this.props.setNextBounds(bounds);
        this.setState({selectedHolder:value, holderLoading:false, message:`Loaded ${featureCollection.features.length} feature${featureCollection.features.length === 1 ? '' : 's'} for ${value}.`});
      } else {
        this.setState({selectedHolder:value, holderLoading:false, error:'The holder loaded, but its geometry could not be zoomed safely.'});
      }
    })
    .catch(error => {
      console.error('Error loading holder features:', error);
      this.setState({holderLoading:false, error: statusText(error, 'Unable to load tenement holder geometry.')});
    })
  }

  render() {
    return (
      <div className="TenementHolderSearchContainer">
        <div className="TenementDirectoryHeader">
          <div>
            <div className="TenementDirectoryEyebrow">Tenement holders</div>
            <h3>Search by holder</h3>
          </div>
          <button type="button" className="TenementsHolderCancelBtn" onClick={this.props.cancel}>Back</button>
        </div>
        <div className="TenementSearchInputRow">
          <input aria-label="Search tenement holders" placeholder="Type a holder name" onChange={(e)=>{this.searchHolders(e.target.value)}}/>
        </div>
        <StatusMessage>{this.state.loading ? 'Loading tenement holders…' : this.state.message}</StatusMessage>
        <StatusMessage type="error">{this.state.error}</StatusMessage>
        <div className="TenementHolderSearchResultContainer">
          {!this.state.loading && this.state.searchResults.length === 0 ? <div className="TenementDirectoryEmpty">No holders match your search.</div> : null}
          {this.state.searchResults.map((value) => (
            <button type="button" disabled={this.state.holderLoading} onClick={()=>{this.getHolderFeature(value)}} className={"TenementHolderSearchResult" + (this.state.selectedHolder === value ? " selected" : "")} key={value}>{value}</button>
          ))}
        </div>
      </div>
    )
  }
}


class TenementsWizard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tenements:[],
      selectedTenement:"",
      dataDates:[],
      date1:null,
      date2:null,
      geoJSON:{},
      geoJSONFiltered:{},
      holdersFiltered:[],
      droppedData:[],
      addedData:[],
      showSubmitRequest:false,
      tenementSearch:false,
      loadingTenements:false,
      loadingDates:false,
      comparisonLoading:false,
      tenementsError:null,
      dataDatesError:null,
      comparisonError:null,
      comparisonMessage:null
    };
    this.getTenements = this.getTenements.bind(this);
    this.getTenementDataDates = this.getTenementDataDates.bind(this);
    this.fetchGeoJSON = this.fetchGeoJSON.bind(this);
    this.filterHolder = this.filterHolder.bind(this);
    this.updateMap = this.updateMap.bind(this);
    this.submitRequest = this.submitRequest.bind(this);
    this.getComparison = this.getComparison.bind(this);
    this.mergeTenementsGeoJSON = this.mergeTenementsGeoJSON.bind(this);
  }
  //NOTE: React Update
  componentDidMount() {
    this.getTenements();
  }

  componentWillUnmount() {
    this.props.clearGeojson();
  }

  //Tenements is an array with just the name and ID. In the future will add support for groups.
  getTenements() {
    this.setState({loadingTenements:true, tenementsError:null});

    axios.get('/tenements/')
    .then(response => {
      const tenements = normaliseTenementsList(response.data);
      this.setState({
        tenements,
        loadingTenements:false,
        tenementsError: tenements.length === 0 ? 'No tenements are available right now.' : null
      });
    })
    .catch(error => {
      console.error('Error loading tenements:', error);
      this.setState({loadingTenements:false, tenementsError: statusText(error, 'Unable to load tenements.')});
    })
  }

  //Gets all the dates that data is available for that tenement.
  getTenementDataDates() {
    if (!this.state.selectedTenement) return

    this.setState({loadingDates:true, dataDatesError:null, dataDates:[], date1:null, date2:null});

    axios.get(`/tenements/${this.state.selectedTenement}`)
    .then(response => {
      const dataDates = normaliseDateList(response.data);
      this.setState({
        dataDates,
        loadingDates:false,
        dataDatesError: dataDates.length === 0 ? 'No extract dates are available for this tenement.' : null
      });
    })
    .catch(error => {
      console.error('Error loading tenement dates:', error);
      this.setState({loadingDates:false, dataDatesError: statusText(error, 'Unable to load extract dates.')});
    })
  }

  getAvailableExtracts() {
    axios.get(`/tenements/${this.state.selectedTenement}/compare?compareDate=${formatDate(this.state.compareDate)}`)
    .then(response => {
      let existingExtractDates = response.data.extracts.map(str => {return new Date(str.date)});
      let extractsUrls = {};
      response.data.extracts.map(extract => {
        let tFormat = formatDate(extract.date);
        extractsUrls[tFormat] = extract.url;
      })
      this.setState({extractsUrls:extractsUrls, existingExtractsDates:existingExtractDates});
    })
    .catch(error => {
      console.log(error);
    })
  }

  getComparison() {
    if (!this.state.selectedTenement || !this.state.date1 || !this.state.date2) {
      return;
    }

    this.setState({comparisonLoading:true, comparisonError:null, comparisonMessage:null});

    axios.get(`/tenements/${this.state.selectedTenement}/comparev2?date1=${formatDate(this.state.date1)}&date2=${formatDate(this.state.date2)}`)
      .then(response => {
        this.fetchGeoJSON({
          dropped:response.data && response.data.dropped,
          added:response.data && response.data.added
        });
      })
      .catch(error => {
        console.error('Error loading tenement comparison:', error);
        if (error.response && error.response.status === 404) {
          this.setState({showSubmitRequest:true, comparisonLoading:false});
        } else {
          this.setState({comparisonLoading:false, comparisonError: statusText(error, 'Unable to load tenement comparison.')});
        }
      })
  }

  fetchGeoJSON(urlObject) {
    if (!urlObject || !urlObject.dropped || !urlObject.added) {
      this.setState({comparisonLoading:false, comparisonError:'Comparison data is missing map URLs.'});
      return;
    }

    Promise.all([
      axios.get(urlObject.dropped, geoServerRequestOptions),
      axios.get(urlObject.added, geoServerRequestOptions)
    ])
    .then(([tenementsDroppedResponse, tenementsAddedResponse]) => {
      this.mergeTenementsGeoJSON(tenementsDroppedResponse.data, tenementsAddedResponse.data);
    })
    .catch(error => {
      console.error('Error loading tenement GeoJSON:', error);
      this.setState({comparisonLoading:false, comparisonError: statusText(error, 'Unable to load comparison geometry.')});
    })
  }

  mergeTenementsGeoJSON(dGeoJSON, aGeoJSON) {
    const droppedCollection = getFeatureCollection(dGeoJSON, 'Dropped tenements');
    const addedCollection = getFeatureCollection(aGeoJSON, 'Added tenements');
    const droppedFeatures = droppedCollection ? droppedCollection.features : [];
    const addedFeatures = addedCollection ? addedCollection.features : [];

    const mergedGeoJSON = {
      type:"FeatureCollection",
      name:"TenementDifferences",
      crs:(dGeoJSON && dGeoJSON.crs) || (aGeoJSON && aGeoJSON.crs) || {
        type:"name",
        properties:{
          name:"urn:ogc:def:crs:EPSG::4283"
        }
      },
      features:[]
    };

    const markedDropped = droppedFeatures.map(feature => ({
      ...feature,
      properties: {...(feature.properties || {}), _INVENTUM_GROUP: "DROPPED"}
    }));

    const markedAdded = addedFeatures.map(feature => ({
      ...feature,
      properties: {...(feature.properties || {}), _INVENTUM_GROUP: "ADDED"}
    }));

    mergedGeoJSON.features = mergedGeoJSON.features.concat(markedDropped, markedAdded);

    if (mergedGeoJSON.features.length === 0) {
      this.setState({comparisonLoading:false, comparisonError:'No changed tenement geometry was returned for those dates.', geoJSON:{}, addedData:[], droppedData:[]});
      return;
    }

    this.setState({geoJSON:mergedGeoJSON, comparisonLoading:false, comparisonMessage:`Loaded ${markedAdded.length} added and ${markedDropped.length} dropped tenement${mergedGeoJSON.features.length === 1 ? '' : 's'}.`},() => {
      this.props.clearGeojson();
      this.props.loadGeojson(mergedGeoJSON);
      const bounds = calculateFeatureCollectionBounds(mergedGeoJSON);
      if (bounds) {
        this.props.setNextBounds(bounds);
      }
      this.processHolders();

    })
  }

  processHolders() {
    let droppedData = [];
    let addedData = [];

    this.state.geoJSON.features.map(feature => {
      const properties = feature.properties || {};
      let tProps = {
        id:properties.FMT_TENID || properties.TENID || '—',
        holder:properties.HOLDER1 || properties.ALL_HOLDER || 'Unknown holder',
        type:properties.TYPE || '—',
        survey_status:properties.SURVSTATUS || '—',
        tenement_status:properties.TENSTATUS || '—',
        end_date:properties.ENDDATE || '—',
        bounds:calculateFeatureBounds(feature)
      };

      if (properties._INVENTUM_GROUP === "DROPPED") {
        droppedData.push(tProps);
      }else {
        addedData.push(tProps);
      }
    })
    let sortFunc = (a, b) => alphaSort(a.holder, b.holder);
    droppedData.sort(sortFunc);
    addedData.sort(sortFunc);
    this.setState({droppedData:droppedData,addedData:addedData});
  }

  filterHolder(name) {
    let holdersFiltered = this.state.holdersFiltered.slice();
    let geoJSONFiltered = {};

    let index = holdersFiltered.indexOf(name);
    if (index === -1) {
      holdersFiltered.push(name)
    }else {
      holdersFiltered.splice(index, 1);
    }

    if (holdersFiltered.length > 0 && this.state.geoJSON.features) {
      geoJSONFiltered = Object.assign({}, this.state.geoJSON);
      geoJSONFiltered.features = geoJSONFiltered.features.filter(feature => {
        return holdersFiltered.includes(feature.properties && feature.properties.HOLDER1)
      })
    }

    this.setState({geoJSONFiltered:geoJSONFiltered,holdersFiltered:holdersFiltered},this.updateMap)
  }

  updateMap() {
    this.props.clearGeojson();

    if (this.state.geoJSONFiltered && Object.keys(this.state.geoJSONFiltered).length > 0) {
      this.props.loadGeojson(this.state.geoJSONFiltered);
    } else if (this.state.geoJSON && this.state.geoJSON.features) {
      this.props.loadGeojson(this.state.geoJSON);
    }
  }

  submitRequest() {
    let onSubmit = response => {
      if (response.data.message === "Request Underway") {
        alert('Job Submitted. Check back in 5-10 minutes');
      }else if (response.status === 202) {
        alert('Job is already being processed. Please wait several minutes');
      }else {
        alert('Job has been processed. Please reload the tenement viewer to load it.')
      }
      console.log(response);
    }

    let handleError = error => {
      console.log(error);
      this.setState({comparisonError: statusText(error, 'Unable to submit comparison request.')});
    }

    //Submit request for Dropped Tenements
    axios.post(`/tenements/${this.state.selectedTenement}/compare`,{compareDate:formatDate(this.state.date1),extractDate:formatDate(this.state.date2)})
    .then(onSubmit)
    .catch(handleError)

    //Submit request for Added Tenements (Reverse Dates)
    axios.post(`/tenements/${this.state.selectedTenement}/compare`,{compareDate:formatDate(this.state.date2),extractDate:formatDate(this.state.date1)})
    .then(onSubmit)
    .catch(handleError)

  }

  render() {
    let handleTenementSelect = (id) => {
      this.setState({selectedTenement:id, dataDates:[], date1:null, date2:null, comparisonError:null, comparisonMessage:null},() => {
        this.getTenementDataDates();
      })
    }

    let handleDate1Select = (date) => {
      this.setState({date1:date, date2:null, comparisonError:null, comparisonMessage:null})
    }

    let handleDate2Select = (date) => {
      this.setState({date2:date,geoJSON:{},addedData:[],droppedData:[],showSubmitRequest:false,comparisonError:null,comparisonMessage:null},() => {
        this.props.clearGeojson();
        this.getComparison();
      });
    }

    if (this.state.tenementSearch) {
      return (
        <div className="TenementsDirectoryContainer">
          <TenementSearch cancel={()=>{this.setState({tenementSearch:false})}} setNextBounds={this.props.setNextBounds} clearGeojson={this.props.clearGeojson} loadGeojson={this.props.loadGeojson}/>
        </div>
      )
    }

    return (
      <div className="TenementsDirectoryContainer">
        <OptionsForm {...this.state}
        handleTenementSelect={handleTenementSelect}
        handleDate1Select={handleDate1Select}
        handleDate2Select={handleDate2Select}
        enableTenementSearch={()=>{this.setState({tenementSearch:true})}}
        />
        {this.state.showSubmitRequest ? <SubmitRequest handleSubmit={this.submitRequest} /> : null}
        <StatusMessage>{this.state.comparisonLoading ? 'Loading comparison geometry…' : this.state.comparisonMessage}</StatusMessage>
        <StatusMessage type="error">{this.state.tenementsError || this.state.dataDatesError || this.state.comparisonError}</StatusMessage>
        <ResultsTable setNextBounds={this.props.setNextBounds} extractDate={this.state.extractDate} compareDate={this.state.compareDate} holdersFiltered={this.state.holdersFiltered} handleFilter={this.filterHolder} droppedData={this.state.droppedData} addedData={this.state.addedData} />
      </div>
    )
  }
}

function OptionsForm(props) {
  return (
    <div className="TenementDirectoryPanel">
      <div className="TenementDirectoryHeader">
        <div>
          <div className="TenementDirectoryEyebrow">Directory</div>
          <h3>Tenement comparison</h3>
        </div>
        <button type="button" className="TenementHolderSearchButton" onClick={props.enableTenementSearch}>Search holder</button>
      </div>
      <TenementSelect loading={props.loadingTenements} selected={props.selectedTenement} tenements={props.tenements} handleSelect={props.handleTenementSelect} />
      <div className="TenementDatePanel">
        <div className="TenementDirectorySectionTitle">Calculate differences between two extracts</div>
          <div className="TenementDateSelectorRow">
            <DateSelector
            selectedDate={props.date1}
            dataAvailable={props.dataDates}
            disabled={!props.selectedTenement || props.loadingDates}
            handleSelect={props.handleDate1Select}
            guide="Older date"
            placeholder={props.loadingDates ? 'Loading…' : 'Older date'}
            />
            <DateSelector
            highlightDates={props.existingExtractsDates}
            excludeDates={[props.compareDate]}
            selectedDate={props.date2}
            dataAvailable={props.dataDates}
            disabled={props.date1 === null || props.loadingDates}
            handleSelect={props.handleDate2Select}
            guide="Recent date"
            placeholder="Recent date"
            />
        </div>
      </div>
    </div>
  )
}

let SubmitRequest = (props) => {
  return (
    <div className="TenementSubmitRequest">The comparison you selected doesn't exist. <button type="button" onClick={props.handleSubmit}>Submit request</button></div>
  )
}

let TenementSelect = (props) => {
  return (
    <div className="TenementSelectBlock">
      <label>Tenement</label>
      <select value={props.selected} disabled={props.loading || props.tenements.length === 0} onChange={(e)=>{props.handleSelect(e.target.value)}}>
        <option value={""} disabled>{props.loading ? 'Loading tenements…' : 'Select a tenement'}</option>
        {props.tenements.map(tenement => <option key={tenement.id} value={tenement.id}>{tenement.name}</option>)}
      </select>
    </div>
  )
}

let DateSelector = (props) => {
  return (
    <div className="TenementDateSelector">
      <label>{props.guide}</label>
      <DatePicker
      disabled={props.disabled}
      onChange={props.handleSelect}
      includeDates={props.dataAvailable}
      excludeDates={props.excludeDates}
      selected={props.selectedDate}
      highlightDates={props.highlightDates}
      showMonthDropdown
      placeholderText={props.placeholder}
      dateFormat="dd/MM/yyyy"
      />
     </div>
  )
}


class ResultsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {category:"DROPPED"};
  }

  toggleCategory(category) {
    this.setState({category})
  }

  render() {
    const rows = this.state.category === "ADDED" ? this.props.addedData : this.props.droppedData;

    return (
      <div className="TenementsWizardResultsContainer">
        <div className="TenementResultsToolbar">
          <button type="button" onClick={() => {this.toggleCategory("DROPPED")}} className={this.state.category === "DROPPED" ? 'active' : ''}>Dropped <span>{this.props.droppedData.length}</span></button>
          <button type="button" onClick={() => {this.toggleCategory("ADDED")}} className={this.state.category === "ADDED" ? 'active' : ''}>Added <span>{this.props.addedData.length}</span></button>
        </div>
        <div className="HoldersList">
          {rows.length === 0 ? <div className="TenementDirectoryEmpty">Select two dates to view changed tenements.</div> : (
            <table className="TenementResultsTable">
              <thead>
                <tr>
                  <th>Holder</th>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Survey</th>
                  <th>Status</th>
                  <th>End date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((data, index) => <Result setNextBounds={this.props.setNextBounds} key={`${data.id}-${index}`} {...data}/>)}
              </tbody>
            </table>
          )}
        </div>
      </div>
    )
  }
}

function Result(props) {
  let handleClick = () => {
    if (props.bounds) {
      props.setNextBounds(props.bounds);
    }
  }
  return (
    <tr>
      <td><button type="button" disabled={!props.bounds} onClick={handleClick}>{props.holder}</button></td>
      <td><button type="button" disabled={!props.bounds} onClick={handleClick}>{props.id}</button></td>
      <td>{props.type}</td>
      <td>{props.survey_status}</td>
      <td>{props.tenement_status}</td>
      <td>{props.end_date}</td>
    </tr>
  )
}

let mapDispatchToProps = dispatch => ({
  closeModal:() => {
    dispatch(closeModal());
  },
  loadGeojson:(data) => {
    dispatch(loadGeojson(data));
  },
  clearGeojson:() => {
    dispatch(clearGeojson());
  },
  toggleMarkers:() => {
    dispatch(toggleMarkers());
  },
  showMarkers:() => {
    dispatch(showMarkers());
  },
  toggleTenementsLayer:() => {
    dispatch(toggleTenementsLayer());
  },
  hideTenementsLayer:() => {
    dispatch(hideTenementsLayer());
  },
  setNextBounds:(data) => {
    dispatch(setNextBounds(data));
  }
})

let mapStateToProps = (state) => ({
  markersVisible:state.mapData.markersVisible,
  tenementsLayerVisible:state.mapData.tenementsLayerVisible
})

const TenementDirectoryContainer = connect(mapStateToProps,mapDispatchToProps)(TenementDirectory)

export default TenementDirectoryContainer
